/**
 * useJobAccess Hook
 * 
 * Custom hook to check if a user has access to a job
 * Handles owner check, public check, and access grant check
 */

import { useState, useEffect } from 'react';
import { checkJobAccess } from '../utils/accessGrantHelpers';

/**
 * Check if user has access to view a job
 * 
 * @param {Object} user - Current user object
 * @param {Object} job - Job object to check access for
 * @returns {Object} Access status with loading state
 */
export const useJobAccess = (user, job) => {
  const [accessStatus, setAccessStatus] = useState({
    loading: true,
    hasAccess: false,
    reason: null,
    permissions: null,
    isOwner: false,
    isPublic: false
  });

  useEffect(() => {
    // If no user or job, no access
    if (!user || !job) {
      setAccessStatus({
        loading: false,
        hasAccess: false,
        reason: 'not_authenticated',
        permissions: null,
        isOwner: false,
        isPublic: false
      });
      return;
    }

    const checkAccess = async () => {
      try {
        // 1. Check if user is owner
        if (job.userId === user.uid) {
          setAccessStatus({
            loading: false,
            hasAccess: true,
            reason: 'owner',
            permissions: {
              viewAnalysis: true,
              viewVideo: true,
              downloadVideo: false,
              exportData: true,
              screenshotFrames: true
            },
            isOwner: true,
            isPublic: job.isPublic || false
          });
          return;
        }

        // 2. Check if job is public
        if (job.isPublic) {
          setAccessStatus({
            loading: false,
            hasAccess: true,
            reason: 'public',
            permissions: {
              viewAnalysis: true,
              viewVideo: true,
              downloadVideo: false,
              exportData: true,
              screenshotFrames: true
            },
            isOwner: false,
            isPublic: true
          });
          return;
        }

        // 3. Check for access grant
        const result = await checkJobAccess(user.uid, job.id);
        
        setAccessStatus({
          loading: false,
          hasAccess: result.hasAccess,
          reason: result.reason,
          permissions: result.permissions,
          isOwner: false,
          isPublic: false,
          grant: result.grant
        });

      } catch (error) {
        console.error('Error checking job access:', error);
        setAccessStatus({
          loading: false,
          hasAccess: false,
          reason: 'error',
          permissions: null,
          isOwner: false,
          isPublic: false,
          error: error.message
        });
      }
    };

    checkAccess();
  }, [user, job]);

  return accessStatus;
};

export default useJobAccess;

